import { OnlyOfficeEditor } from "@/components/common/OnlyOfficeEditor.tsx";
import ConfirmWithPasswordModal from "@/components/confirm-with-password-modal";
import Alert from "@/components/core/alert";
import { Blanket, BlanketImage, BlanketInfo } from "@/components/core/blanket";
import { Button } from "@/components/core/buttons/button";
import { Checkbox } from "@/components/core/forms/checkbox";
import { CheckboxGroup } from "@/components/core/forms/checkbox-group";
import Combobox from "@/components/core/forms/combobox";
import ComboboxCreatable from "@/components/core/forms/combox-creatable";
import { Form, FormControl, FormDescription, FormField, FormGroup, FormItem, FormLabel, FormMessage } from "@/components/core/forms/form.tsx";
import { Input } from "@/components/core/forms/input.tsx";
import Select from "@/components/core/forms/select";
import TagsInput from "@/components/core/forms/tags-input";
import { Textarea } from "@/components/core/forms/textarea";
import { useModal } from "@/components/core/modals/use-modal";
import { useModalControl } from "@/components/core/modals/use-modal-controller";
import { ScrollArea } from "@/components/core/scroller";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/core/tabs";
import Tooltip from "@/components/core/tooltip";
import MoveToInformationModal from "@/components/move-to-information-modal";
import { PdfViewer } from "@/components/pdf-viewer";
import { caseStages } from "@/constants/case-stages";
import { caseUserRoles } from "@/constants/case-user-roles";
import { userRoles } from "@/constants/user-roles";
import { caseStagesQueryOptions } from "@/features/case-record/api/queries/case-stage-query";
import type { CaseRecord } from "@/features/case-record/types/case-records.ts";
import type { CaseStage } from "@/features/case-record/types/case-stage";
import { useDocumentConfigMutation } from "@/features/dashboard/api/queries/document-config-queries.ts";
import eventService from "@/features/dashboard/api/services/event-service";
import { useAuth } from "@/hooks/use-auth";
import { queryClient } from "@/query-client";
import { zodResolver } from "@hookform/resolvers/zod";
import { IconAlertCircle, IconCirclePlus, IconEye, IconFile, IconTemplate, IconTrash, IconX } from "@tabler/icons-react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { format } from "date-fns";
import { debounce, set } from "lodash";
import moment from "moment";
import { type ChangeEvent, act, useCallback, useEffect, useRef, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import { useCaseActivityMutation, useCaseConvertDocsMutation } from "../api/queries/acitivities-query";
import { useEvidenceMutation } from "../api/queries/evidence-query";
import caseEvidenceService from "../api/services/evidence-service";
import formTemplateService from "../api/services/form-templates-service";
import { type CaseActivity, type CaseActivityForm, caseActivityFormSchema } from "../types/activities";
import EvidenceList from "./evidence-list";

export default function CaseActivityModal({
	caseRecord,
	data,
	title = "",
	moveTo = false,
	decision = false,
	allowPostAs = false,
	nextStage = null,
	collaborators = [],
	isSummon = false,
	nextVisibleStage = null,
	isSheriffReport = false,
	isDismissal = false,
}: {
	caseRecord: CaseRecord;
	nextStage: CaseStage | null;
	nextVisibleStage: CaseStage | null;
	data: CaseActivity;
	title: string;
	moveTo: boolean;
	allowPostAs: boolean;
	decision: boolean;
	isSummon: boolean;
	isSheriffReport: boolean;
	collaborators: any[];
	isDismissal: boolean;
}) {
	const modal = useModal();
	const navigate = useNavigate();
	const { hasRole } = useAuth();
	const { closeModal } = useModalControl();
	const documentTokenMutation = useDocumentConfigMutation();
	const caseActivitiesMutation = useCaseActivityMutation();
	const convertDocsMutation = useCaseConvertDocsMutation();
	const [compliance, setCompliance] = useState(isSummon || data?.activity === "Notice of Pre-Trial Conference");
	const [deadline, setDeadline] = useState(isSummon || data?.activity === "Notice of Pre-Trial Conference");
	const [inCalendar, setInCalendar] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [defaultDefendants, setDefaultDefendants] = useState<any[]>([]);

	const getRole = (role: string) => {
		if (role === caseUserRoles.DEFENDANT) {
			return caseRecord?.defendant_label;
		}
		if (role === caseUserRoles.PLAINTIFF) {
			return caseRecord?.plaintiff_label;
		}

		return role;
	};

	const respondentsMap = caseRecord.collaborators
		.filter((role: { role: string }) => role.role === "Defendant")
		.map((item: { user: { full_name: string }; role: string }) => ({
			label: `${item?.full_name} - ${item.role}`,
			value: item,
		}));
	const respondentsBoth = caseRecord.collaborators
		.filter((role: { role: string }) => role.role === "Defendant" || role.role === "Plaintiff")
		.map((item: { user: { full_name: string }; role: string }) => ({
			label: `${item?.full_name} - ${item.role}`,
			value: item,
		}));

	const localDate = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Manila" }));

	const yyyy = localDate.getFullYear();
	const mm = String(localDate.getMonth() + 1).padStart(2, "0");
	const dd = String(localDate.getDate()).padStart(2, "0");
	const hh = String(localDate.getHours()).padStart(2, "0");
	const min = String(localDate.getMinutes()).padStart(2, "0");
	const ss = String(localDate.getSeconds()).padStart(2, "0");
	const form = useForm<CaseActivityForm>({
		resolver: zodResolver(caseActivityFormSchema),
		defaultValues: {
			id: data?.id,
			evidences: [],
			case_id: data?.case_id || caseRecord.id,
			document_key: "",
			counter_claim: "none",
			party_type: hasRole([userRoles.JUDGE]) ? "Judge" : "Bcc",
			stage: data?.stage,
			activity_id: data?.activity_id,
			assigned_to: data?.assigned_to || "Both",
			activity: isSummon ? "Summons" : data?.activity === "Judgment" ? "Decision" : data?.activity,
			summons: isSummon ? "true" : null,
			tag: isSummon ? "Summons" : isSheriffReport ? "Sheriff's Report" : data?.activity,
			format_type: "upload_files",
			type: "Custom",
			due_at: null,
			remarks: data?.remarks,
			move_to_next_stage: moveTo,
			attachment: null,
			calendar_event: null,
			ordered_at: null,
			sheriff_report: null,
			defendants: [],
			submitted_at: null,
			action_type: null,
			manner_of_service: null,
			service_status: null,
			service_reason: null,
			scheduled_at: null,
			filed_at: `${yyyy}-${mm}-${dd} ${hh}:${min}:${ss}`,
			close_case: false,
			in_calendar: data.in_calendar || false,
			respondents: isSummon ? [respondentsMap[0]] : data?.activity === "Notice of Pre-Trial Conference" ? respondentsBoth : null,
			// form_template_id: data.form_template_id || null,
			with_decision: decision || data?.activity === "Judgment" || false,
			decision_at: data?.decision_at || null,
			with_fees: data?.with_fees || false,
			with_grant: data?.with_grant || false,
			with_compliance: isSummon || data?.activity === "Notice of Pre-Trial Conference" ? true : data?.with_compliance || false,
			with_deadline: isSummon || data?.activity === "Notice of Pre-Trial Conference" ? true : data?.with_deadline || false,
			decision: decision || false,
			hearings: [
				{
					title: data?.activity || "Hearing",
					type: data?.activity || "Hearing",
					assigned_to: "Both",
					remarks: data?.remarks || data?.activity?.toLowerCase().replace(/-/g, " ") === "notice of pre trial conference" ? "Pre-Trial Conference" : "",
					due_date: data?.due_at || "",
					hearing_mode: "",
					stage: nextVisibleStage ? nextVisibleStage?.name : data?.stage,
				},
			],
		},
	});

	const inputRef = useRef<HTMLInputElement | null>(null);
	const [multipleFiles, setMultipleFiles] = useState<File[]>([]);
	const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
	const [selectedFile, setSelectedFile] = useState<string | null>(null);
	const [tabValue, setTabValue] = useState("activity-details");
	const [showAttachment, setShowAttachment] = useState(false);
	const [isTrialOrder, setIsTrialOrder] = useState(data?.activity === "Pre-Trial Order" || false);
	const [partyTypeReference, setPartyTypeReference] = useState("Bcc");

	const { watch } = form;
	const evidences = form.watch("evidences");
	const serviceStatus = watch("service_status");
	const isNextStage = watch("move_to_next_stage");
	const sheriffReport = watch("sheriff_report");
	const hearingInput = watch("hearings");
	const activity = watch("activity");
	const withDecision = watch("with_decision");

	const [docOpts, setDocOpts] = useState({
		token: "",
		options: {},
	});

	const formatType = watch("format_type");
	const [type, setType] = useState(null);
	const [matchedStage, setMatchedStage] = useState<CaseStage | null>(null);

	const caseStageQuery = useSuspenseQuery(caseStagesQueryOptions());
	const caseStagesNew = caseStageQuery.data;
	const updateDate = (days: number) => {
		const today = new Date();
		const dateDue = new Date(today.setDate(today.getDate() + days));
		const date = `${dateDue.toISOString().split("T")[0]}`;
		form.setValue("decision_at", date);
	};

	const handleRemoveAttachment = (index: number) => {
		setSelectedFiles((prevFiles) => {
			const updatedFiles = prevFiles.filter((_, i) => i !== index);
			form.setValue("attachment", updatedFiles);
			setMultipleFiles(updatedFiles);
			setShowAttachment(updatedFiles.length > 0);
			return updatedFiles;
		});
	};
	const baseUrl = (import.meta.env.VITE_PUBLIC_API_BASE_URL || "http://localhost:8000").replace(/\/api$/, "");

	const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
		const files = event.target.files;
		if (files && files.length > 0) {
			const file = files[0];
			const objectUrl = URL.createObjectURL(file);
			if (file && file.type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document") {
				const responseDocs = await convertDocsMutation.mutateAsync(file);

				const response = await documentTokenMutation.mutateAsync({
					file_type: "docx",
					title: responseDocs?.path,
					url: `${baseUrl}${responseDocs?.url}`,
					mode: "view",
				});

				setDocOpts({
					token: response.token,
					options: response.options,
				});

				setDocument(responseDocs);
			} else {
				setDocument(objectUrl);
			}
			setSelectedFiles((prevFiles) => {
				const newFiles = [...prevFiles, ...Array.from(files)];
				form.setValue("attachment", newFiles);
				setMultipleFiles(newFiles);
				setShowAttachment(true);
				return newFiles;
			});
		}
	};

	const submit = async (values) => {
		setIsSubmitting(true);
		try {
			await caseActivitiesMutation.mutateAsync(values);
			await queryClient.invalidateQueries({ queryKey: ["case_activities"] });
			await queryClient.invalidateQueries({ queryKey: ["case_records"] });
			if (decision) {
				await navigate({
					to: `/case-evaluation/${caseRecord.id}`,
					search: (old) => ({
						...old,
						stage: "POST_JUDGMENT",
					}),
					replace: true,
				});
			} else {
				if (isNextStage && nextStage?.name !== null && nextStage?.is_button !== true) {
					if (nextStage?.is_judgment) {
						await navigate({
							to: `/case-evaluation/${caseRecord.id}`,
							search: (old) => ({
								...old,
								stage: "POST_JUDGMENT",
							}),
							replace: true,
						});
					} else {
						await navigate({
							to: `/case-evaluation/${caseRecord.id}`,
							search: (old) => ({
								...old,
								stage: nextStage?.name,
							}),
							replace: true,
						});
					}
				} else {
					await navigate({
						to: `/case-evaluation/${caseRecord.id}`,
						search: (old) => ({
							...old,
							stage: nextStage?.next_stage,
						}),
						replace: true,
					});
				}
			}

			closeModal();
		} catch (error) {
			setIsSubmitting(false);
		}
	};

	const [isHoliday, setIsHoliday] = useState(false);
	const storedTime = sessionStorage.getItem("password_entered_at");

	const handleDraft = async (values: CaseActivityForm) => {
		const isValid = await form.trigger();
		form.setValue("state", "draft");
		if (isValid) {
			handleSubmit(form.getValues());
		}
	};

	const handleSubmit = async (values: CaseActivityForm) => {
		console.log("asd");
		const defendantsLength = values.defendants?.length || 0;
		const updatedValues = {
			...values,
			length: defendantsLength,
		};

		const expirationTime = moment(storedTime).add(5, "minutes");
		if (storedTime && expirationTime !== undefined && moment().isBefore(expirationTime)) {
			submit(updatedValues);
		} else {
			modal.openModal({
				allowClose: true,
				title: "Confirm Submit",
				description: "Are you sure? This action cannot be undone.",
				size: "sm",
				showHeader: true,
				showFooter: false,
				component: (
					<ConfirmWithPasswordModal
						onSubmit={async () => {
							await submit(updatedValues);
						}}
					/>
				),
			});
		}
	};

	const openModal = () => {
		modal.openModal({
			allowClose: true,
			title: "Move to Information",
			size: "md",
			showHeader: true,
			showFooter: false,
			component: <MoveToInformationModal />,
		});
	};

	const [document, setDocument] = useState<string | null>(null);

	const hearings = useFieldArray({
		control: form.control,
		name: "hearings",
	});

	const [debouncedDate, setDebouncedDate] = useState("");
	const [rowCounts, setRowCount] = useState(0);
	const [parties, setParties] = useState<string>();
	const [matchWord, setMatchWord] = useState<string | null>(null);
	const [closeAlert, setCloseAlert] = useState(false);

	const handleAddRow = () => {
		hearings.append({
			title: "Hearing",
			type: "Hearing",
			assigned_to: "Both",
			remarks: "",
			due_date: "",
			stage: nextVisibleStage ? nextVisibleStage?.name : data?.stage,
		});
	};

	const hearingDate = form.watch("hearings").map((hearing: { due_date: string }) => {
		return hearing.due_date;
	});

	const updateDebouncedDate = useCallback(
		debounce(
			(newDate) => {
				setDebouncedDate(newDate);
			},
			500,
			{ trailing: true },
		),
		[],
	);

	useEffect(() => {
		if (hearingDate) {
			const dates = Array.isArray(hearingDate) ? hearingDate : [hearingDate];

			let hasEmptyDate = false;

			dates.map((data) => {
				if (!data || data.trim() === "") {
					hasEmptyDate = true;
				} else {
					const parsedDate = new Date(data);
					if (!Number.isNaN(parsedDate.getTime())) {
						updateDebouncedDate(format(parsedDate, "yyyy-MM-dd"));
					}
				}
			});
			if (hasEmptyDate) {
				setRowCount(hearings.fields.length - 1);
			} else {
				setRowCount(hearings.fields.length);
			}
		}
	}, [hearingDate, updateDebouncedDate]);

	// dialog box warning for browser refresh / close tab
	useEffect(() => {
		const handleBeforeUnload = (e) => {
			e.preventDefault();
			e.returnValue = "";
		};

		window.addEventListener("beforeunload", handleBeforeUnload);

		return () => {
			window.removeEventListener("beforeunload", handleBeforeUnload);
		};
	}, []);

	useEffect(() => {
		if (debouncedDate) {
			eventService
				.validateHolidays(debouncedDate)
				.then((response) => {
					if (response.id) {
						setIsHoliday(true);
					} else {
						setIsHoliday(false);
					}
				})
				.catch((_) => {
					setIsHoliday(false);
				});
		}
	}, [debouncedDate, hearingDate]);

	useEffect(() => {
		if (formatType === "text_editor") {
			documentTokenMutation
				.mutateAsync({
					file_type: "docx",
					title: `New Document for Case No.${caseRecord.case_number}`,
					mode: "edit",
				})
				.then((response) => {
					setDocOpts({
						token: response.token,
						options: response.options,
					});

					form.setValue("document_key", response.key);
				});
		} else {
			// we have to reset the document key since we don't want the backend to assume
			// we're still trying to upload a document from the text-editor
			form.setValue("document_key", null);
		}
	}, [formatType]);

	useEffect(() => {
		documentTokenMutation
			.mutateAsync({
				file_type: "docx",
				title: `New Document for Case No.${caseRecord.case_number}`,
				mode: "edit",
			})
			.then((response) => {
				setDocOpts({
					token: response.token,
					options: response.options,
				});

				form.setValue("document_key", response.key);
			});
	}, []);

	const counterClaim = form.watch("counter_claim");
	const [multiplePaymentFiles, setMultiplePaymentFiles] = useState<File[]>([]);
	const [showPaymentAttachment, setShowPaymentAttachment] = useState({
		attachment: false,
		proof_of_payment: false,
	});

	const proofRef = useRef<HTMLInputElement | null>(null);

	useEffect(() => {
		if (counterClaim === "none" || counterClaim === "compulsory") {
			setMultiplePaymentFiles([]);
			form.setValue("proof_of_payment", []);
			setShowPaymentAttachment((prev) => ({
				...prev,
				proof_of_payment: false,
				attachment: multipleFiles.length > 0,
			}));

			setSelectedFile(multipleFiles[0]);
		}
	}, [counterClaim, multipleFiles.length, form]);

	function formatNames(names: string[]): string {
		if (names.length === 0) return "";
		if (names.length === 1) return names[0];
		if (names.length === 2) return `${names[0]} and ${names[1]}`;
		return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1].replace(" - Defendant", "").replace(" - Plaintiff", "").trim()}`;
	}

	const respondentsInput = form.watch("respondents");
	useEffect(() => {
		if (isSummon) {
			const names = formatNames(respondentsInput.map((respondent: { label: string }) => `${respondent.label}`));
			form.setValue("activity", `Summons - ${names.replace(" - Defendant", "").replace(" - Plaintiff", "").trim()}`);
		}
	}, [respondentsInput]);

	useEffect(() => {
		if (activity && nextVisibleStage) {
			const stage = nextVisibleStage;
			let foundStage: Stage | null = null;

			for (const stage of caseStagesNew.data) {
				for (const keywordObj of stage.keywords) {
					let keywords: string[] = [];

					try {
						keywords = JSON.parse(keywordObj.keyword).map((w: string) => w.toLowerCase());
					} catch {
						continue;
					}
					const labelWords = keywords.map((w: string) => w.toLowerCase());
					const activityWords = activity.toLowerCase().split(/[\s,]+/);
					const matchCount = keywords.filter((word) => activityWords.includes(word)).length;

					if (labelWords.length === matchCount) {
						foundStage = stage;
						break;
					}
				}
				if (foundStage?.name !== caseRecord.stage) {
					setMatchedStage(foundStage?.label);
				} else {
					setMatchedStage(false);
				}
			}

			let hasMatch = false;
			for (let index = 0; index < stage.keywords.length; index++) {
				const element = stage.keywords[index];
				const labelWords = (JSON.parse(element.keyword) || []).map((w: string) => w.toLowerCase());
				const activityWords = (activity || "").toLowerCase().split(/[\s,]+/);
				const matchCount = labelWords.filter((word: string) => activityWords.includes(word)).length;
				if (labelWords.length === matchCount) {
					hasMatch = true;
				}
			}
			if (!hasMatch) {
				setMatchWord(null);
				if (moveTo === false) {
					form.setValue("move_to_next_stage", false);
				}
			} else {
				if (moveTo === false) {
					setMatchedStage(null);
					setMatchWord(stage?.button_alias || null);
					form.setValue("move_to_next_stage", true);
				}
			}
		}
	}, [activity]);

	const handleProofFileChange = (event: ChangeEvent<HTMLInputElement>) => {
		const proofFiles = event.target.files;
		if (proofFiles && proofFiles.length > 0) {
			const newFiles = Array.from(proofFiles);
			setSelectedFile(newFiles[0]);

			setMultiplePaymentFiles((prevFiles) => {
				const uniqueFiles = [...prevFiles];

				// biome-ignore lint/complexity/noForEach: <explanation>
				newFiles.forEach((file) => {
					if (!prevFiles.some((f) => f.name === file.name && f.size === file.size)) {
						uniqueFiles.push(file);
					}
				});

				form.setValue("proof_of_payment", uniqueFiles);
				setShowAttachment((prev) => ({ ...prev, proof_of_payment: true }));
				return uniqueFiles;
			});
		}
	};

	const defendants = caseRecord.collaborators;
	const [postingForDefendant, setPostingForDefendant] = useState<string>(defendants[0]?.id);

	const expungeCaseMutation = useEvidenceMutation();
	const activeDefendantId = postingForDefendant || defendants[0]?.id;
	// const matchedStage = useMatchStageFromActivity(activity, stages);

	// useEffect(() => {
	//   if (matchedStage) {
	//     console.log("Matched stage:", matchedStage.label);
	//   }
	// }, [matchedStage]);

	return (
		<Form {...form}>
			<form className="w-full h-full" onSubmit={form.handleSubmit(handleSubmit)}>
				<div className="flex flex-col w-full h-full">
					<div className="flex shrink-0 flex-row items-center justify-between w-full h-[50px] px-4 border-b border-slate-200">
						<span className="text-base font-bold text-foreground-dark">{title?.replace(/_/g, " ")}</span>
						<button
							onClick={() => {
								closeModal();
							}}
							className="flex w-[20px] h-[20px] ring-offset-background transition-opacity outline-none border-none ring-0 text-foreground opacity-40 hover:opacity-100 disabled:pointer-events-none"
							type="button"
						>
							<IconX size={20} stroke={1.5} />
						</button>
					</div>

					<div className="flex flex-col w-full h-full">
						<Tabs value={tabValue} className="flex h-full" variant="compact">
							<TabsList className="pt-4">
								<TabsTrigger value="activity-details" onClick={() => setTabValue("activity-details")}>
									Details
								</TabsTrigger>
								{activity?.toLowerCase() !== "answer to complaint" && (
									<TabsTrigger value="hearing" onClick={() => setTabValue("hearing")}>
										Hearing
									</TabsTrigger>
								)}
								{activity?.toLowerCase() === "answer to complaint".toLowerCase() && allowPostAs && (
									<TabsTrigger value="pieces-of-evidence" onClick={() => setTabValue("pieces-of-evidence")}>
										Pieces of Evidence
									</TabsTrigger>
								)}
							</TabsList>
							<TabsContent className="h-[calc(100svh-170px)]" value="activity-details">
								<PanelGroup className="flex flex-row grow w-full" direction="horizontal">
									<Panel defaultSize={30} minSize={30} maxSize={30} className="flex flex-col h-full min-w-[420px] max-w-[420px]">
										<div className="flex flex-col justify-between bg-background">
											<ScrollArea className="h-[calc(100svh-170px)]">
												<fieldset disabled={isSubmitting}>
													<div className="grid grid-cols-12 w-full p-4 gap-x-3 gap-y-2">
														<div className="col-span-12 w-full">
															<FormField
																control={form.control}
																name="format_type"
																render={({ field }) => (
																	<FormItem>
																		<div className="flex flex-col w-full">
																			<FormLabel required>Select Format Type</FormLabel>
																		</div>
																		<FormGroup>
																			<Select
																				onValueChange={async (value) => {
																					field.onChange(value);
																				}}
																				defaultValue={field.value}
																				options={[
																					{
																						value: "upload_files",
																						label: "Upload Files",
																					},
																				]}
																			/>
																		</FormGroup>
																		<FormMessage />
																	</FormItem>
																)}
															/>
														</div>
														{formatType === "text_editor" && (
															<div className="col-span-12 w-full">
																<FormField
																	control={form.control}
																	name="type"
																	render={({ field }) => (
																		<FormItem>
																			<div className="flex flex-col w-full">
																				<FormLabel required>Select Type</FormLabel>
																			</div>
																			<FormGroup>
																				<Select
																					onValueChange={async (value) => {
																						field.onChange(value);
																						setType(value);
																						if (value === "Custom") {
																							const response = await documentTokenMutation.mutateAsync({
																								file_type: "docx",
																								title: "New Document",
																								url: "https://sc-admin.revlv.com/blank.docx",
																								mode: "edit",
																							});
																							form.setValue("document_key", response.key);
																							setDocOpts({
																								token: response.token,
																								options: response.options,
																							});
																						}
																					}}
																					defaultValue={field.value}
																					options={[
																						{
																							value: "Custom",
																							label: "Custom",
																						},
																						{
																							value: "Template",
																							label: "Template",
																						},
																					]}
																				/>
																			</FormGroup>
																			<FormMessage />
																		</FormItem>
																	)}
																/>
															</div>
														)}
														{formatType === "text_editor" && type === "Template" && (
															<div className="col-span-12 w-full">
																<FormField
																	control={form.control}
																	name="form_template_id"
																	render={({ field }) => (
																		<FormItem>
																			<div className="flex flex-col w-full">
																				<FormLabel>Court Actions</FormLabel>
																			</div>
																			<FormGroup>
																				<ComboboxCreatable
																					defaultValue={field.form_template_id}
																					onValueChange={async (value) => {
																						const response = await documentTokenMutation.mutateAsync({
																							file_type: "docx",
																							title: value.medias.name,
																							url: value.medias.original_url,
																							mode: "edit",
																						});

																						form.setValue("form_template_id", value.value);

																						// form.setValue("activity", value.label);
																						form.setValue("document_key", response.key);
																						setDocOpts({
																							token: response.token,
																							options: response.options,
																						});
																					}}
																					loadOptions={async (value: string) => {
																						const items = await formTemplateService.list({
																							"filter[search]": value || data.activity,
																						});

																						return items.data.map((item) => ({
																							label: item.title,
																							value: item.id,
																							...item,
																						}));
																					}}
																				/>
																			</FormGroup>
																			<FormMessage />
																		</FormItem>
																	)}
																/>
															</div>
														)}
														<div className="col-span-12 w-full">
															<FormField
																control={form.control}
																name="activity"
																render={({ field }) => (
																	<FormItem>
																		<div className="flex flex-col w-full">
																			<FormLabel required>Title</FormLabel>
																			<FormDescription />
																		</div>
																		<FormGroup>
																			<Input autoComplete="off" placeholder="Title" {...field} />
																		</FormGroup>
																		<FormMessage />
																	</FormItem>
																)}
															/>
															{!closeAlert && allowPostAs && activity?.toLowerCase() === "answer to complaint".toLowerCase() && (
																<Alert
																	className="mt-2"
																	title={"Are you trying to create Answer to Complaint?"}
																	type="info"
																	allowClose={false}
																	icon={<IconAlertCircle size={20} stroke={2} />}
																>
																	<div className="flex flex-row items-center w-full gap-2">
																		<button type="button" onClick={() => {}}>
																			see more...
																		</button>
																	</div>
																	<div className="flex flex-row items-center w-full gap-2">
																		<Button
																			type="button"
																			size={"sm"}
																			variant={"green"}
																			onClick={() => {
																				form.setValue("action_type", "with_compliance");
																				form.setValue("with_compliance", true);
																				form.setValue("with_grant", false);
																				setCloseAlert(true);
																			}}
																		>
																			Yes
																		</Button>
																		<Button
																			type="button"
																			size={"sm"}
																			variant={"danger"}
																			onClick={() => {
																				form.setValue("activity", "");
																				setCloseAlert(true);
																			}}
																		>
																			No
																		</Button>
																	</div>
																</Alert>
															)}
															{matchWord &&
																((!decision && !moveTo && nextStage?.is_visible && !isSummon) ||
																	(nextStage?.is_button && !moveTo) ||
																	(nextStage?.is_judgment && !moveTo)) && (
																	<Alert
																		className="mt-2"
																		title={`Are you trying to create ${matchWord}?`}
																		type="info"
																		allowClose={false}
																		icon={<IconAlertCircle size={20} stroke={2} />}
																	>
																		<div className="flex flex-row items-center w-full gap-2">
																			<button
																				type="button"
																				onClick={() => {
																					openModal();
																				}}
																			>
																				see more...
																			</button>
																		</div>
																		<div className="flex flex-row items-center w-full gap-2">
																			<Button
																				type="button"
																				size={"sm"}
																				variant={"green"}
																				onClick={() => {
																					setMatchWord(null);
																				}}
																			>
																				Yes
																			</Button>
																			<Button
																				type="button"
																				size={"sm"}
																				variant={"danger"}
																				onClick={() => {
																					form.setValue("activity", "");
																					form.setValue("move_to_next_stage", false);
																					setMatchWord(null);
																				}}
																			>
																				No
																			</Button>
																		</div>
																	</Alert>
																)}
															{matchedStage &&
																((!decision && !moveTo && nextStage?.is_visible && !isSummon) ||
																	(nextStage?.is_button && !moveTo) ||
																	(nextStage?.is_judgment && !moveTo)) && (
																	<Alert
																		className="mt-2"
																		title={`Creating ${matchedStage} is not allowed in your current stage.`}
																		type="error"
																		allowClose={false}
																		icon={<IconAlertCircle size={20} stroke={2} />}
																	/>
																)}
														</div>

														{formatType === "upload_files" && (
															<div className="col-span-12 flex w-full">
																<FormField
																	control={form.control}
																	name="attachment"
																	render={() => (
																		<FormItem>
																			<div className="flex flex-col w-full">
																				<FormLabel>Upload Files</FormLabel>
																			</div>
																			<FormGroup>
																				<div className="flex flex-col w-full rounded-md border border-border">
																					<div className="relative flex flex-row items-center w-full h-[38px] max-h-[38px] p-1.5">
																						<input
																							type="file"
																							accept=".pdf,.doc,.docx"
																							ref={inputRef}
																							className="absolute inset-0 w-full h-full cursor-pointer opacity-0"
																							multiple
																							onChange={handleFileChange}
																						/>
																						<div className="flex flex-row items-center w-full gap-2">
																							<div className="inline-flex flex-row items-center px-3 h-[28px] gap-1 rounded bg-primary-600 font-semibold text-primary-foreground cursor-pointer">
																								<span className="text-sm">{multipleFiles.length > 0 ? "Add" : "Browse"}</span>
																							</div>
																							<span className="text-base text-foreground">
																								{multipleFiles.length === 1
																									? multipleFiles[0].name
																									: multipleFiles.length > 1
																										? "More than 2 files selected"
																										: "No file selected"}
																							</span>
																						</div>
																					</div>

																					{showAttachment && multipleFiles.length > 0 && (
																						<div className="flex flex-col w-full p-1.5 gap-2 border-t border-border">
																							{multipleFiles.map((file, index) => (
																								<div key={index} className="flex flex-row justify-between items-center">
																									<div className="items-center flex flex-row gap-2">
																										<div className="inline-flex shrink-0 justify-center items-center w-[32px] h-[32px] rounded-md bg-slate-200 text-foreground overflow-hidden">
																											<IconFile size={18} stroke={2} />
																										</div>
																										<div className="inline-flex flex-col">
																											<span className="truncate max-w-[240px] text-sm font-semibold text-foreground-dark">{file.name}</span>
																											<span className="text-sm text-foreground/50">{(file.size / 1024).toFixed(2)} KB</span>
																										</div>
																									</div>

																									<div className="inlineflex flex-row w-auto">
																										<Tooltip className="!z-[999]" content="Preview">
																											<button
																												className="inline-flex justify-center items-center w-[28px] h-[28px] ml-auto rounded-md text-foreground/50 hover:text-foreground"
																												type="button"
																												color="red"
																												onClick={async () => {
																													const objectUrl = URL.createObjectURL(file);
																													if (file && file.type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document") {
																														const responseDocs = await convertDocsMutation.mutateAsync(file);

																														const response = await documentTokenMutation.mutateAsync({
																															file_type: "docx",
																															title: responseDocs?.path,
																															url: `${baseUrl}${responseDocs?.url}`,
																															mode: "view",
																														});

																														setDocOpts({
																															token: response.token,
																															options: response.options,
																														});

																														setDocument(responseDocs);
																													} else {
																														setDocument(objectUrl);
																													}
																												}}
																											>
																												<IconEye size={18} stroke={2} />
																											</button>
																										</Tooltip>
																										<Tooltip className="!z-[999]" content="Remove">
																											<button
																												className="inline-flex justify-center items-center w-[28px] h-[28px] ml-auto rounded-md text-danger-600 hover:text-danger-500"
																												type="button"
																												color="red"
																												onClick={() => handleRemoveAttachment(index)}
																											>
																												<IconTrash size={18} stroke={2} />
																											</button>
																										</Tooltip>
																									</div>
																								</div>
																							))}
																						</div>
																					)}
																				</div>
																			</FormGroup>
																			<FormMessage />
																		</FormItem>
																	)}
																/>
															</div>
														)}

														{!isSummon && (
															<div className="col-span-12 w-full">
																<FormField
																	control={form.control}
																	name="assigned_to"
																	render={({ field }) => (
																		<FormItem>
																			<div className="flex flex-col w-full">
																				<FormLabel required>Recipient</FormLabel>
																			</div>
																			<FormGroup>
																				<Select
																					onValueChange={async (value) => {
																						field.onChange(value);
																					}}
																					defaultValue={field.value}
																					options={[
																						{
																							value: "Both",
																							label: "Both",
																						},
																						{
																							value: "Plaintiff",
																							label: "Plaintiff",
																						},
																						{
																							value: "Defendant",
																							label: "Defendant",
																						},
																					]}
																				/>
																			</FormGroup>
																			<FormMessage />
																		</FormItem>
																	)}
																/>
															</div>
														)}

														{activity !== "Sheriff's Report / Process Server's Report" &&
															activity !== "Writ of Execution" &&
															!moveTo &&
															!isSummon &&
															allowPostAs &&
															activity?.toLowerCase() !== "answer to complaint" && (
																<div className="col-span-12 w-full">
																	<FormField
																		control={form.control}
																		name="party_type"
																		render={({ field }) => (
																			<FormItem>
																				<div className="flex flex-col w-full">
																					<FormLabel required>Post As</FormLabel>
																				</div>
																				<FormGroup>
																					<Combobox
																						defaultValue={field.value}
																						onValueChange={(value) => {
																							if (value) {
																								field.onChange(value.value);
																								setPartyTypeReference(value.user_id ? value.user_id : value.value);
																								const postingsFor = collaborators
																									.filter(
																										(item) =>
																											item.user_id === value.user_id || item.counsels?.some((counsel) => counsel.user_id === value.user_id),
																									)
																									.map((item) => ({
																										label: `${item?.full_name} - ${item.role}`,
																										value: item.id,
																									}));
																								if (postingsFor.length === 1) {
																									setDefaultDefendants(postingsFor);
																								}
																								if (caseRecord.case_classification === "Small Claims") {
																									setDefaultDefendants([value]);
																								}
																							}
																						}}
																						returnFullOption={true}
																						loadOptions={async () => {
																							const baseOption = [
																								{
																									value: "Bcc",
																									label: "BCC",
																									reference_id: "Bcc",
																								},
																							];

																							const filteredCollaborators =
																								caseRecord.case_classification === "Small Claims"
																									? collaborators.filter((item) => item.role === "Plaintiff" || item.role === "Defendant")
																									: collaborators.filter((item) => item.role === "Counsel (P)" || item.role === "Counsel (D)");

																							const seenNames = new Set();
																							const collaboratorOptions = filteredCollaborators
																								.filter((item) => {
																									if (seenNames.has(item.full_name)) {
																										return false;
																									}
																									seenNames.add(item.full_name);
																									return true;
																								})
																								.map((item) => ({
																									label: ` ${caseRecord.case_classification !== "Small Claims" ? "Atty." : ""} ${item?.full_name} - ${getRole(item.role)}`,
																									value: item.id,
																									user_id: item.user_id,
																									role: item.role,
																									reference_id: item.reference_id,
																								}));

																							return [...baseOption, ...collaboratorOptions];
																						}}
																					/>
																				</FormGroup>
																				<FormMessage />
																			</FormItem>
																		)}
																	/>
																</div>
															)}

														{title !== "POST_JUDGMENT" && title !== "JUDGMENT" && partyTypeReference === "Bcc" && (
															<div className="col-span-12 flex flex-col w-full">
																<FormField
																	control={form.control}
																	name="with_fees"
																	render={() => (
																		<FormItem>
																			<div className="flex flex-col w-full">
																				<FormLabel>Options</FormLabel>
																			</div>
																			<CheckboxGroup>
																				<FormItem>
																					<FormGroup>
																						<div className="flex flex-row w-full gap-2">
																							<FormControl>
																								<Checkbox
																									size="lg"
																									name="with_compliance"
																									checked={compliance}
																									onCheckedChange={(value) => {
																										setCompliance(value === true);
																										setDeadline(value === true);
																										setInCalendar(value === true);
																										form.setValue("with_compliance", value === true);
																										form.setValue("with_deadline", value === true);
																										form.setValue("in_calendar", value === true);
																									}}
																								/>
																							</FormControl>
																							<FormLabel>
																								<div className="flex flex-col w-full">
																									<span className="text-base text-foreground">With Compliance</span>
																									<span className="text-sm text-muted-foreground">This activity will have compliance.</span>
																								</div>
																							</FormLabel>
																						</div>
																					</FormGroup>
																					<FormMessage />
																				</FormItem>
																				{!isSummon && (
																					<>
																						<FormItem>
																							<FormGroup>
																								<div className="flex flex-row w-full gap-2">
																									<FormControl>
																										<Checkbox
																											size="lg"
																											name="with_deadline"
																											checked={deadline}
																											disabled={!compliance}
																											onCheckedChange={(value) => {
																												setDeadline(value === true);
																												form.setValue("with_deadline", value === true);
																												setInCalendar(value === true);
																												form.setValue("in_calendar", value === true);
																											}}
																										/>
																									</FormControl>
																									<FormLabel>
																										<div className="flex flex-col w-full">
																											<span className="text-base text-foreground">With Deadline</span>
																											<span className="text-sm text-muted-foreground">This activity will have deadline.</span>
																										</div>
																									</FormLabel>
																								</div>
																							</FormGroup>
																							<FormMessage />
																						</FormItem>

																						<FormItem>
																							<FormGroup>
																								<div className="flex flex-row w-full gap-2">
																									<FormControl>
																										<Checkbox
																											size="lg"
																											name="in_calendar"
																											checked={inCalendar}
																											onCheckedChange={(value) => {
																												setInCalendar(value === true);
																												form.setValue("in_calendar", value === true);
																											}}
																										/>
																									</FormControl>
																									<FormLabel>
																										<div className="flex flex-col w-full">
																											<span className="text-base text-foreground">In Calendar</span>
																											<span className="text-sm text-muted-foreground">This activity will be shown in calendar.</span>
																										</div>
																									</FormLabel>
																								</div>
																							</FormGroup>
																							<FormMessage />
																						</FormItem>

																						{!decision &&
																							nextStage?.is_visible &&
																							allowPostAs &&
																							data.stage === caseStages.PRELIMINARY_PROCEEDINGS &&
																							partyTypeReference === "Bcc" && (
																								<FormItem>
																									<FormGroup>
																										<div className="flex flex-row w-full gap-2">
																											<FormControl>
																												<Checkbox
																													size="lg"
																													name="sheriff_report"
																													checked={sheriffReport}
																													onCheckedChange={(value) => {
																														form.setValue("sheriff_report", value);
																													}}
																												/>
																											</FormControl>
																											<FormLabel>
																												<div className="flex flex-col w-full">
																													<span className="text-base text-foreground">Sheriff's Report</span>
																													<span className="text-sm text-muted-foreground">This will add sherrif report.</span>
																												</div>
																											</FormLabel>
																										</div>
																									</FormGroup>
																									<FormMessage />
																								</FormItem>
																							)}
																					</>
																				)}
																			</CheckboxGroup>
																			<FormMessage />
																		</FormItem>
																	)}
																/>
															</div>
														)}

														{inCalendar && !isSummon && (
															<div className="col-span-12 w-full">
																<FormField
																	control={form.control}
																	name="calendar_event"
																	render={({ field }) => (
																		<FormItem>
																			<div className="flex flex-col w-full">
																				<FormLabel required>Calendar Event</FormLabel>
																			</div>
																			<FormGroup>
																				<Input autoComplete="off" placeholder="Calendar Event" {...field} value={field.value ?? ""} />
																			</FormGroup>
																			<FormMessage />
																		</FormItem>
																	)}
																/>
															</div>
														)}

														{inCalendar && !isSummon && (
															<div className="col-span-12 w-full">
																<FormField
																	control={form.control}
																	name="scheduled_at"
																	render={({ field }) => (
																		<FormItem>
																			<div className="flex flex-col w-full">
																				<FormLabel required>Calendar Date</FormLabel>
																			</div>
																			<FormGroup>
																				{/* <FormCalendar showTimePicker={true} showIcon={true} currentDate={null} {...field} /> */}
																				<Input autoComplete="off" type="date" placeholder="Calendar Date" {...field} />
																			</FormGroup>
																			<FormMessage />
																		</FormItem>
																	)}
																/>
															</div>
														)}

														{deadline && !isSummon && (
															<div className="col-span-12 w-full">
																<FormField
																	control={form.control}
																	name="due_at"
																	render={({ field }) => (
																		<FormItem>
																			<div className="flex flex-col w-full">
																				<FormLabel required>Deadline</FormLabel>
																			</div>
																			<FormGroup>
																				{/* <FormCalendar showTimePicker={true} showIcon={true} currentDate={null} {...field} /> */}
																				<Input autoComplete="off" type="date" placeholder="Deadline" {...field} />
																			</FormGroup>
																			<FormMessage />
																		</FormItem>
																	)}
																/>
															</div>
														)}

														{compliance && (
															<div className="col-span-12 w-full">
																<FormField
																	control={form.control}
																	name="respondents"
																	render={({ field }) => (
																		<FormItem>
																			<div className="flex flex-col w-full">
																				<FormLabel required>To be Complied By</FormLabel>
																			</div>
																			<FormGroup>
																				<TagsInput
																					isMulti={true}
																					defaultValue={field.value}
																					onValueChange={(value) => {
																						field.onChange(value);
																					}}
																					loadOptions={async () =>
																						caseRecord.collaborators
																							.filter(
																								(item: { role: string }) => item.role === caseUserRoles.DEFENDANT || item.role === caseUserRoles.PLAINTIFF,
																							)
																							.map((item: { id: string; user: { full_name: string }; role: string }) => ({
																								label: `${item?.full_name} - ${item.role}`,
																								value: item.id,
																							}))
																					}
																				/>
																			</FormGroup>
																			<FormMessage />
																		</FormItem>
																	)}
																/>
															</div>
														)}

														{!moveTo && !isSummon && allowPostAs && activity?.toLowerCase() === "answer to complaint".toLowerCase() && (
															<div className="col-span-12 w-full">
																<FormField
																	control={form.control}
																	name="party_type"
																	render={({ field }) => (
																		<FormItem>
																			<div className="flex flex-col w-full">
																				<FormLabel required>Post As</FormLabel>
																			</div>
																			<FormGroup>
																				<Combobox
																					defaultValue={field.value}
																					onValueChange={(value) => {
																						if (value) {
																							field.onChange(value.value);
																							setPartyTypeReference(value.user_id);
																							const postingsFor = collaborators
																								.filter(
																									(item) =>
																										item.user_id === value.user_id || item.counsels?.some((counsel) => counsel.user_id === value.user_id),
																								)
																								.map((item) => ({
																									label: `${item?.full_name} - ${item.role}`,
																									value: item.id,
																								}));
																							if (postingsFor.length === 1) {
																								setDefaultDefendants(postingsFor);
																							}
																							if (caseRecord.case_classification === "Small Claims") {
																								setDefaultDefendants([value]);
																							}
																						}
																					}}
																					returnFullOption={true}
																					loadOptions={async () => {
																						const filteredCollaborators =
																							caseRecord.case_classification === "Small Claims"
																								? collaborators.filter((item) => item.role === "Defendant")
																								: collaborators.filter((item) => item.role === "Counsel (D)");

																						const collaboratorOptions = filteredCollaborators.map((item) => ({
																							label: ` ${caseRecord.case_classification !== "Small Claims" ? "Atty." : ""} ${item?.full_name} - ${getRole(item.role)}`,
																							value: item.id,
																							user_id: item.user_id,
																							role: item.role,
																							reference_id: item.reference_id,
																						}));

																						return collaboratorOptions;
																					}}
																				/>
																			</FormGroup>
																			<FormMessage />
																		</FormItem>
																	)}
																/>
															</div>
														)}

														{!moveTo && !isSummon && allowPostAs && partyTypeReference !== "Bcc" && caseRecord.case_classification !== "Small Claims" && (
															<div className="col-span-12 w-full">
																<FormField
																	key={partyTypeReference}
																	control={form.control}
																	name="defendants"
																	render={({ field }) => (
																		<FormItem>
																			<div className="flex flex-col w-full">
																				<FormLabel required>Posting For</FormLabel>
																			</div>
																			<FormGroup>
																				<FormControl>
																					<TagsInput
																						isMulti={true}
																						defaultValue={defaultDefendants}
																						onValueChange={(value) => {
																							field.onChange(value);
																						}}
																						loadOptions={async () =>
																							collaborators
																								.filter((item) => item.counsels?.some((counsel) => counsel.user_id === partyTypeReference))
																								.map((item) => ({
																									label: `${item?.full_name} - ${item.role}`,
																									value: item.id,
																								}))
																						}
																					/>
																				</FormControl>
																			</FormGroup>
																			<FormMessage />
																		</FormItem>
																	)}
																/>
															</div>
														)}

														{allowPostAs && activity?.toLowerCase() === "answer to complaint".toLowerCase() && partyTypeReference !== "Bcc" && (
															<>
																<div className="col-span-12 w-full">
																	<FormField
																		control={form.control}
																		name="counter_claim"
																		render={({ field }) => (
																			<FormItem>
																				<div className="flex flex-col w-full">
																					<FormLabel required>Counter Claim</FormLabel>
																				</div>
																				<FormGroup>
																					<Select
																						onValueChange={async (value) => {
																							field.onChange(value);
																							// if (value === "with_grant") {
																							// 	form.setValue("with_grant", true);
																							// 	form.setValue("with_compliance", false);
																							// }
																							// if (value === "with_compliance") {
																							// 	form.setValue("with_compliance", true);
																							// 	form.setValue("with_grant", false);
																							// }
																						}}
																						defaultValue={field.value}
																						options={[
																							{
																								value: "none",
																								label: "None",
																							},
																							{
																								value: "both",
																								label: "Both",
																							},
																							{
																								value: "compulsory",
																								label: "Compulsory",
																							},
																							{
																								value: "permissive",
																								label: "Permissive",
																							},
																						]}
																					/>
																				</FormGroup>
																				<FormMessage />
																			</FormItem>
																		)}
																	/>
																</div>

																{(counterClaim === "permissive" || counterClaim === "both") && (
																	<div className="col-span-12 flex w-full">
																		<FormField
																			control={form.control}
																			name="proof_of_payment"
																			render={() => (
																				<FormItem>
																					<div className="flex flex-col w-full">
																						<FormLabel>Proof of Payment</FormLabel>
																					</div>
																					<FormGroup>
																						<div className="flex flex-col w-full rounded-md border border-border">
																							<div className="relative flex flex-row items-center w-full h-[38px] max-h-[38px] p-1.5">
																								<input
																									type="file"
																									ref={proofRef}
																									className="absolute inset-0 w-full h-full cursor-pointer opacity-0"
																									multiple
																									onChange={handleProofFileChange}
																								/>
																								<div className="flex flex-row items-center w-full gap-2">
																									<div className="inline-flex flex-row items-center px-3 h-[28px] gap-1 rounded bg-primary-600 font-semibold text-primary-foreground cursor-pointer">
																										<span className="text-sm">{multiplePaymentFiles.length > 0 ? "Add" : "Browse"}</span>
																									</div>
																									<span className="text-base text-foreground">
																										{multiplePaymentFiles.length === 1
																											? multiplePaymentFiles[0].name
																											: multiplePaymentFiles.length > 1
																												? "More than 2 files selected"
																												: "No file selected"}
																									</span>
																								</div>
																							</div>

																							{showAttachment.proof_of_payment && multiplePaymentFiles.length > 0 && (
																								<div className="flex flex-col w-full p-1.5 gap-2 border-t border-border">
																									{multiplePaymentFiles.map((file, index) => (
																										<div key={index} className="flex flex-row justify-between items-center">
																											<div className="items-center flex flex-row gap-2">
																												<div className="inline-flex shrink-0 justify-center items-center w-[32px] h-[32px] rounded-md bg-slate-200 text-foreground overflow-hidden">
																													<IconFile size={18} stroke={2} />
																												</div>
																												<div className="inline-flex flex-col">
																													<span className="truncate max-w-[240px] text-sm font-semibold text-foreground-dark">{file.name}</span>
																													<span className="text-sm text-foreground/50">{(file.size / 1024).toFixed(2)} KB</span>
																												</div>
																											</div>

																											<div className="inlineflex flex-row w-auto">
																												<Tooltip className="!z-[999]" content="Preview">
																													<button
																														className="inline-flex justify-center items-center w-[28px] h-[28px] ml-auto rounded-md text-foreground/50 hover:text-foreground"
																														type="button"
																														color="red"
																														onClick={() => {
																															handlePreview(file);
																														}}
																													>
																														<IconEye size={18} stroke={2} />
																													</button>
																												</Tooltip>
																												<Tooltip className="!z-[999]" content="Remove">
																													<button
																														className="inline-flex justify-center items-center w-[28px] h-[28px] ml-auto rounded-md text-danger-600 hover:text-danger-500"
																														type="button"
																														color="red"
																														onClick={() => handleRemovePayments(index)}
																													>
																														<IconTrash size={18} stroke={2} />
																													</button>
																												</Tooltip>
																											</div>
																										</div>
																									))}
																								</div>
																							)}
																						</div>
																					</FormGroup>
																					<FormMessage />
																				</FormItem>
																			)}
																		/>
																	</div>
																)}
															</>
														)}

														{!moveTo && !isSummon && allowPostAs && partyTypeReference !== "Bcc" && (
															<div className="col-span-12 w-full">
																<FormField
																	control={form.control}
																	name="action_type"
																	render={({ field }) => (
																		<FormItem>
																			<div className="flex flex-col w-full">
																				<FormLabel required>Select Action</FormLabel>
																			</div>
																			<FormGroup>
																				<Select
																					onValueChange={async (value) => {
																						field.onChange(value);
																						if (value === "with_grant") {
																							form.setValue("with_grant", true);
																							form.setValue("with_compliance", false);
																						}
																						if (value === "with_compliance") {
																							form.setValue("with_compliance", true);
																							form.setValue("with_grant", false);
																						}
																					}}
																					defaultValue={field.value}
																					options={[
																						{
																							value: "with_grant",
																							label: "Motions",
																						},
																						{
																							value: "with_compliance",
																							label: "Pleadings/Other Submissions",
																						},
																					]}
																				/>
																			</FormGroup>
																			<FormMessage />
																		</FormItem>
																	)}
																/>
															</div>
														)}

														{/* {!moveTo && !isSummon && allowPostAs && partyTypeReference !== "Bcc" && (
														<div className="col-span-12 w-full">
															<FormField
																control={form.control}
																name="submitted_at"
																render={({ field }) => (
																	<FormItem>
																		<div className="flex flex-col w-full">
																			<FormLabel>Date Submitted</FormLabel>
																		</div>
																		<FormGroup>
																			<Input autoComplete="off" type="datetime-local" placeholder="Date Submitted" {...field} />
																		</FormGroup>
																		<FormMessage />
																	</FormItem>
																)}
															/>
														</div>
													)} */}
														{partyTypeReference === "Bcc" ? (
															<div className="col-span-12 w-full">
																<FormField
																	control={form.control}
																	name="ordered_at"
																	render={({ field }) => (
																		<FormItem>
																			<div className="flex flex-col w-full">
																				<FormLabel required>
																					{data.activity !== "Sheriff's Report / Process Server's Report" ? "Date Ordered" : "Date of Report"}
																				</FormLabel>
																			</div>
																			<FormGroup>
																				<Input autoComplete="off" type="datetime-local" placeholder="Date Ordered" {...field} />
																			</FormGroup>
																			<FormMessage />
																		</FormItem>
																	)}
																/>
															</div>
														) : (
															<div className="col-span-12 w-full">
																<FormField
																	control={form.control}
																	name="filed_at"
																	render={({ field }) => (
																		<FormItem>
																			<div className="flex flex-col w-full">
																				<FormLabel required>Date Filed</FormLabel>
																			</div>
																			<FormGroup>
																				<Input autoComplete="off" type="datetime-local" placeholder="Date Filed" {...field} />
																			</FormGroup>
																			<FormMessage />
																		</FormItem>
																	)}
																/>
															</div>
														)}

														{data.activity === "Sheriff's Report / Process Server's Report" && caseRecord.stage !== caseStages.POST_JUDGMENT && (
															<div className="col-span-12 w-full">
																<FormField
																	control={form.control}
																	name="service_status"
																	render={({ field }) => (
																		<FormItem>
																			<div className="flex flex-col w-full">
																				<FormLabel required>Service Status</FormLabel>
																			</div>
																			<FormGroup>
																				<Select
																					placeholder=""
																					onValueChange={field.onChange}
																					defaultValue={field.value}
																					options={[
																						{
																							value: "Served",
																							label: "Served",
																						},
																						{
																							value: "Unserved",
																							label: "Unserved",
																						},
																					]}
																				/>{" "}
																			</FormGroup>
																			<FormMessage />
																		</FormItem>
																	)}
																/>
															</div>
														)}
														{data.activity === "Sheriff's Report / Process Server's Report" && serviceStatus === "Served" && (
															<div className="col-span-12 w-full">
																<FormField
																	control={form.control}
																	name="service_date"
																	render={({ field }) => (
																		<FormItem>
																			<div className="flex flex-col w-full">
																				<FormLabel required>Service Date</FormLabel>
																			</div>
																			<FormGroup>
																				<Input autoComplete="off" type="date" placeholder="Service Date" {...field} />
																			</FormGroup>
																			<FormMessage />
																		</FormItem>
																	)}
																/>
															</div>
														)}

														{data.activity === "Sheriff's Report / Process Server's Report" && serviceStatus === "Unserved" && (
															<div className="col-span-12 w-full">
																<FormField
																	control={form.control}
																	name="service_reason"
																	render={({ field }) => (
																		<FormItem>
																			<div className="flex flex-col w-full">
																				<FormLabel required>Reason</FormLabel>
																			</div>
																			<FormGroup>
																				<Textarea {...field} value={field.value ?? ""} />
																			</FormGroup>
																			<FormMessage />
																		</FormItem>
																	)}
																/>
															</div>
														)}

														<div className="col-span-12 w-full ">
															<FormField
																control={form.control}
																name="remarks"
																render={({ field }) => (
																	<FormItem>
																		<div className="flex flex-col w-full">
																			<FormLabel>Remarks</FormLabel>
																			<FormDescription />
																		</div>
																		<FormGroup>
																			<Textarea {...field} value={field.value ?? ""} />
																		</FormGroup>
																		<FormMessage />
																	</FormItem>
																)}
															/>
														</div>

														{!decision && nextStage?.is_judgment && isNextStage && (
															<div className="col-span-12 w-full">
																<FormField
																	control={form.control}
																	name="with_decision"
																	render={({ field }) => (
																		<FormItem>
																			<FormGroup>
																				<div className="flex flex-row w-full gap-2">
																					<FormControl>
																						<Checkbox size="lg" checked={withDecision} onCheckedChange={field.onChange} />
																					</FormControl>
																					<FormLabel>
																						<div className="flex flex-col w-full">
																							<span className="text-base text-foreground">With Decision</span>
																						</div>
																					</FormLabel>
																				</div>
																			</FormGroup>
																			<FormMessage />
																		</FormItem>
																	)}
																/>
															</div>
														)}
														{isDismissal && (
															<div className="col-span-12 w-full">
																<FormField
																	control={form.control}
																	name="mode_of_dismissal"
																	render={({ field }) => (
																		<FormItem>
																			<div className="flex flex-col w-full">
																				<FormLabel required>Mode of Dismissal</FormLabel>
																			</div>
																			<FormGroup>
																				<Select
																					placeholder=""
																					onValueChange={field.onChange}
																					defaultValue={field.value}
																					options={[
																						{
																							value: "Disposition by Decision",
																							label: "Disposition by Decision",
																						},
																						{
																							value: "Extended Resolution",
																							label: "Extended Resolution",
																						},
																						{
																							value: "Minute Resolution",
																							label: "Minute Resolution",
																						},
																						{
																							value: "Others",
																							label: "Others",
																						},
																					]}
																				/>{" "}
																			</FormGroup>
																			<FormMessage />
																		</FormItem>
																	)}
																/>
															</div>
														)}
														{((withDecision && caseRecord.case_classification !== "Small Claims") || isDismissal) && (
															<div className="col-span-12 w-full">
																<FormField
																	control={form.control}
																	name="decision_at"
																	render={({ field }) => (
																		<FormItem>
																			<div className="flex flex-col w-full">
																				<FormLabel required>Appeal Deadline</FormLabel>
																			</div>
																			<div className="col-span-12 w-full">
																				<Button
																					type="button"
																					variant={"ghost"}
																					onClick={() => {
																						updateDate(15);
																					}}
																				>
																					15D
																				</Button>
																				<Button
																					type="button"
																					className="ml-2"
																					variant={"ghost"}
																					onClick={() => {
																						updateDate(30);
																					}}
																				>
																					30D
																				</Button>
																			</div>
																			<FormItem>
																				<div className="flex flex-col w-full">
																					<FormLabel required>Appeal Deadline</FormLabel>
																				</div>
																				<FormGroup>
																					<Input autoComplete="off" type="date" placeholder="Appeal Deadline" {...field} />
																				</FormGroup>
																				<FormMessage />
																			</FormItem>
																		</FormItem>
																	)}
																/>
															</div>
														)}

														{/* <div className="col-span-12 flex flex-col w-full my-1 gap-3">
															{isNextStage &&
																!moveTo &&
																nextStage?.is_button !== true &&
																data.stage !== caseStages.POST_JUDGMENT &&
																data.stage !== caseStages.PRE_TRIAL_CONFERENCE &&
																data.stage !== caseStages.JUDGMENT && (
																	<Alert
																		type="info"
																		allowClose={false}
																		title={`After submitting, the case will move to ${nextStage?.is_judgment ? "Post Judgment" : nextStage?.label} status.`}
																		icon={<IconAlertCircle size={20} stroke={2} />}
																	/>
																)}
															{decision && (
																<Alert
																	type="info"
																	allowClose={false}
																	title="After submitting, the case will move to Post Judgment status."
																	icon={<IconAlertCircle size={20} stroke={2} />}
																/>
															)}
														</div> */}
													</div>
												</fieldset>
											</ScrollArea>
										</div>
									</Panel>

									<PanelResizeHandle className="group flex justify-center items-center w-[12px] border-r border-l border-border/50 bg-white">
										<div className="inline-flex w-[4px] h-[40px] rounded-sm bg-foreground/50 group-hover:bg-foreground/90" />
									</PanelResizeHandle>

									<Panel defaultSize={70} minSize={70} maxSize={70} className="flex flex-col shrink grow w-full h-full bg-slate-100">
										{formatType === "text_editor" && docOpts?.token && <OnlyOfficeEditor token={docOpts.token} options={docOpts.options} />}

										{formatType === "upload_files" && document?.url && <OnlyOfficeEditor token={docOpts.token} options={docOpts.options} />}

										{formatType === "upload_files" && !document?.url && <PdfViewer file={document} documents={document} />}

										{!formatType && (
											<Blanket className="my-auto self-center" width="sm">
												<BlanketImage icon={<IconTemplate size={24} stroke={1.5} />} />
												<BlanketInfo title="No Selected Template" message="Select Template on Select Type to load the default template for this form." />
											</Blanket>
										)}
									</Panel>
								</PanelGroup>
								{!matchWord && (
									<div className="flex shrink-0 flex-row items-center justify-end w-full h-[65px] px-4 gap-3 border-t border-slate-200 bg-background">
										<Button
											variant="ghost"
											type="button"
											onClick={() => {
												closeModal();
											}}
										>
											Cancel
										</Button>
										{formatType === "text_editor" && !allowPostAs && (
											<Button
												type="button"
												onClick={() => {
													handleDraft(form.getValues());
												}}
												variant={"green"}
												disabled={isSubmitting}
												loading={isSubmitting}
											>
												Save As Draft
											</Button>
										)}

										{activity !== "Pre-Trial Order" &&
											!isTrialOrder &&
											(activity?.toLowerCase() !== "answer to complaint" || !allowPostAs) &&
											activity?.toLowerCase().replace(/-/g, " ") !== "notice of pre trial conference" && (
												<Button disabled={isSubmitting} loading={isSubmitting}>
													Submit
												</Button>
											)}
										{(activity === "Pre-Trial Order" || isTrialOrder) && (activity?.toLowerCase() !== "answer to complaint" || !allowPostAs) && (
											<Button
												onClick={() => {
													setTabValue("hearing");
												}}
											>
												Next
											</Button>
										)}
										{activity?.toLowerCase().replace(/-/g, " ") === "notice of pre trial conference" && (
											<Button
												onClick={() => {
													setTabValue("hearing");
												}}
											>
												Next
											</Button>
										)}
										{activity?.toLowerCase() === "answer to complaint".toLowerCase() && allowPostAs && (
											<Button
												onClick={() => {
													setTabValue("pieces-of-evidence");
												}}
											>
												Next
											</Button>
										)}
									</div>
								)}
							</TabsContent>
							<TabsContent className="h-full" value="hearing">
								<PanelGroup className="flex flex-row grow w-full" direction="horizontal">
									<Panel defaultSize={100} minSize={100} maxSize={100} className="flex flex-col h-full w-full">
										<div className="flex flex-col h-full justify-between">
											<ScrollArea className="h-[calc(100svh-170px)]">
												{isHoliday && (
													<div className="col-span-12 w-full py-1">
														<Alert type="info" allowClose={false} title="The date selected is a Holiday" icon={<IconAlertCircle size={20} stroke={2} />}>
															The selected date falls on a holiday. Please take this into consideration when scheduling this hearing. You can still schedule a
															hearing on this date.
														</Alert>
													</div>
												)}
												<div className="flex flex-col gap-4 p-2">
													{hearings.fields.map((hearing, index) => (
														<div className="border rounded-md w-full p-4" key={index}>
															<div className="flex flex-col">
																<div className="flex flex-row">
																	<div className="flex flex-col w-full gap-3">
																		<div className="flex flex-row items-center justify-between">
																			<div className="flex flex-col w-full">
																				<span className="text-lg font-semibold text-foreground-dark">Hearing # {index + 1}</span>
																			</div>
																			<button
																				className="inline-flex justify-center items-center px-3 text-danger-600 hover:text-danger-500"
																				type="button"
																				onClick={() => hearings.remove(index)}
																			>
																				<IconTrash size={18} stroke={2} />
																			</button>
																		</div>
																		<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
																			<div className="col-span-2 w-full">
																				<FormField
																					control={form.control}
																					name={`hearings[${index}].due_date`}
																					render={({ field }) => (
																						<FormItem>
																							<div className="flex flex-col w-full">
																								<FormLabel required>Date and Time</FormLabel>
																								<FormDescription />
																							</div>
																							<FormGroup>
																								<Input
																									type="datetime-local"
																									value={field.value || ""}
																									onChange={async (e) => {
																										const value = e.target.value;
																										field.onChange(value);
																										const response = await eventService.validateHearing(value, caseRecord.id);
																										form.setValue(`hearings.${index}.hearing_count`, response);
																									}}
																								/>
																							</FormGroup>
																							<FormMessage />
																						</FormItem>
																					)}
																				/>
																			</div>
																			<div className="col-span-3 w-full">
																				<FormField
																					control={form.control}
																					name={`hearings[${index}].remarks`}
																					render={({ field }) => (
																						<FormItem>
																							<div className="flex flex-col w-full">
																								<FormLabel required>Activity/Event</FormLabel>
																								<FormDescription />
																							</div>
																							<FormGroup>
																								<Input autoComplete="off" placeholder="e.g Presentation of Evidence" {...field} />
																							</FormGroup>
																							<FormMessage />
																						</FormItem>
																					)}
																				/>
																			</div>
																			<div className="col-span-3 w-full">
																				<FormField
																					control={form.control}
																					name={`hearings[${index}].stage`}
																					render={({ field }) => (
																						<FormItem>
																							<div className="flex flex-col w-full">
																								<FormLabel required>Stage</FormLabel>
																								<FormDescription />
																							</div>
																							<FormGroup>
																								<Select
																									onValueChange={async (value) => {
																										field.onChange(value);
																									}}
																									defaultValue={field.value}
																									options={caseStagesNew.data
																										.filter((c) => c.is_visible)
																										.map((c) => ({
																											label: c.label,
																											value: c.name,
																										}))}
																								/>
																							</FormGroup>
																						</FormItem>
																					)}
																				/>
																			</div>

																			<div className="col-span-2 w-full">
																				<FormField
																					control={form.control}
																					name={`hearings[${index}].hearing_mode`}
																					render={({ field }) => (
																						<FormItem>
																							<div className="flex flex-col w-full">
																								<FormLabel required>Hearing Mode</FormLabel>
																								<FormDescription />
																							</div>
																							<FormGroup>
																								<Select
																									onValueChange={async (value) => {
																										field.onChange(value);
																									}}
																									defaultValue={field.value}
																									options={[
																										{
																											value: "Face to Face",
																											label: "Face to Face",
																										},
																										{
																											value: "Online",
																											label: "Online",
																										},
																									]}
																								/>
																							</FormGroup>
																							<FormMessage />
																						</FormItem>
																					)}
																				/>
																			</div>
																			<div className="col-span-2 w-full">
																				<FormField
																					control={form.control}
																					name={`hearings.${index}.hearing_count`}
																					render={({ field }) => {
																						return (
																							<FormItem>
																								<div className="flex flex-col w-full">
																									<FormLabel>Settings</FormLabel>
																									<FormDescription />
																								</div>
																								<FormGroup>
																									<Tooltip className="z-[999]" content="Click to open calendar in new tab" side="top">
																										{/* <button
                                      onClick={() => {
                                        window.open(`/calendar?view=true&date=${form.getValues(`hearings.${index}.due_date`)}`);
                                      }}
                                      type="button"
                                      className="text-start border w-full rounded-md block bg-muted text-muted-foreground px-3"
                                    > */}
																										<Button
																											className="w-full !justify-start !containerbg-muted text-muted-foreground"
																											variant="ghost"
																											onClick={() => {
																												window.open(`/calendar?view=true&date=${form.getValues(`hearings.${index}.due_date`)}`);
																											}}
																										>
																											{field.value || 0}
																										</Button>
																										{/* </button> */}
																									</Tooltip>
																								</FormGroup>
																							</FormItem>
																						);
																					}}
																				/>
																			</div>
																			{activity?.toLowerCase().replace(/-/g, " ") !== "notice of pre trial conference" && (
																				<>
																					<div className="col-span-4 w-full">
																						<FormField
																							control={form.control}
																							name={`hearings[${index}].witness_name`}
																							render={({ field }) => (
																								<FormItem>
																									<div className="flex flex-col w-full">
																										<FormLabel>Witness /Judicial Affidavits</FormLabel>
																										<FormDescription />
																									</div>
																									<FormGroup>
																										<FormField
																											control={form.control}
																											name={`hearings[${index}].witness_name`}
																											render={({ field }) => (
																												<TagsInput
																													isMulti={true}
																													defaultValue={field.value}
																													onValueChange={(value) => {
																														field.onChange(value);
																														if (value.length > 0) {
																															setParties(value[0].parties);
																														}
																													}}
																													loadOptions={async (value: string) => {
																														const items = await caseEvidenceService.listAllByCaseId(caseRecord.id, {
																															"filter[evidence_type]": "Testimonial",
																															"filter[case_id]": caseRecord.id,
																															"filter[search]": value,
																														});
																														return items.data.map((item) => ({
																															label: item.witness_name,
																															value: item.id,
																															parties: item.parties,
																															case_record_user_id: item.case_record_user_id || null,
																														}));
																													}}
																												/>
																											)}
																										/>
																									</FormGroup>
																								</FormItem>
																							)}
																						/>
																					</div>

																					<div className="col-span-8 w-full">
																						<FormField
																							control={form.control}
																							name={`hearings[${index}].exhibits`}
																							render={() => (
																								<FormItem>
																									<div className="flex flex-col w-full">
																										<FormLabel>Exhibits</FormLabel>
																										<FormDescription />
																									</div>
																									<FormGroup>
																										<FormField
																											control={form.control}
																											name={`hearings[${index}].exhibits`}
																											render={({ field }) => (
																												<TagsInput
																													key={parties}
																													isMulti={true}
																													defaultValue={field.value}
																													onValueChange={(value) => {
																														field.onChange(value);
																													}}
																													loadOptions={async (value: string) => {
																														const items = await caseEvidenceService.listAllByCaseId(caseRecord.id, {
																															"filter[case_id]": caseRecord.id,
																															"filter[parties]": parties,
																															"filter[search]": value,
																														});
																														return items.data
																															.filter((i) => i.evidence_type !== "Testimonial" && !i.parent_id)
																															.map((item) => ({
																																label: `${item.exhibit} - ${item.description}`,
																																value: item.id,
																																parties: item.parties,
																																witness_name: item.witness_name || null,
																																case_record_user_id: item.case_record_user_id || null,
																															}));
																													}}
																												/>
																											)}
																										/>
																									</FormGroup>
																								</FormItem>
																							)}
																						/>
																					</div>
																				</>
																			)}
																		</div>
																	</div>
																</div>
															</div>
														</div>
													))}

													{activity?.toLowerCase().replace(/-/g, " ") !== "notice of pre trial conference" && (
														<Button type="button" onClick={handleAddRow} size="default" variant="ghost" className="w-full">
															<IconCirclePlus size={18} stroke={2} /> Add New Schedule
														</Button>
													)}
												</div>
											</ScrollArea>

											<div className="flex shrink-0 flex-row items-center justify-end w-full h-[65px] px-4 gap-3 border-t border-slate-200 bg-background">
												<Button
													variant="ghost"
													type="button"
													onClick={() => {
														closeModal();
													}}
												>
													Cancel
												</Button>
												{hearingInput.some((hearing) => hearing.remarks?.trim()) && (
													<Button disabled={isSubmitting} loading={isSubmitting}>
														Submit
													</Button>
												)}
											</div>
										</div>
									</Panel>
								</PanelGroup>
							</TabsContent>

							<TabsContent className="h-full" value="pieces-of-evidence">
								<PanelGroup className="flex flex-row grow w-full" direction="horizontal">
									<Panel defaultSize={100} minSize={100} maxSize={100} className="flex flex-col h-full w-full">
										<div className="flex flex-col w-full h-full justify-between">
											{/* <ScrollArea className="h-[calc(100svh-170px)]"> */}
											<div className="flex flex-col w-full gap-3">
												<div className="flex flex-col w-full rounded-md border border-border/50">
													<div className="flex flex-col w-full">
														<Tabs className="h-full flex flex-row" defaultValue="documents" orientation="vertical">
															<TabsList className="px-4 mt-6">
																<TabsTrigger
																	className="text-sm"
																	value="documents"
																	count={evidences?.filter((e) => e.evidence_type === "Documentary" && e.case_record_user_id === activeDefendantId).length}
																>
																	Documents
																</TabsTrigger>
																<TabsTrigger
																	className="text-sm"
																	value="testimonial"
																	count={evidences?.filter((e) => e.evidence_type === "Testimonial" && e.case_record_user_id === activeDefendantId).length}
																>
																	Testimonial
																</TabsTrigger>
																<TabsTrigger
																	className="text-sm"
																	value="object"
																	count={evidences?.filter((e) => e.evidence_type === "Object" && e.case_record_user_id === activeDefendantId).length}
																>
																	Object
																</TabsTrigger>
															</TabsList>

															<ScrollArea className="h-[calc(100svh-170px)] w-full">
																<TabsContent value="documents" className="data-[orientation=vertical]:ml-0 px-2">
																	<div className="flex flex-col w-full">
																		<EvidenceList
																			canEdit={true}
																			removeFromList={false}
																			data={evidences?.filter((e) => e.evidence_type === "Documentary" && e.case_record_user_id === activeDefendantId) || []}
																			schema={[
																				{
																					className: "w-[90px] py-2 px-3 text-left text-sm text-foreground-dark",
																					name: "exhibit",
																					label: "Exhibit",
																					type: "text_input",
																					placeholder: "Enter exhibit",
																				},
																				{
																					name: "description",
																					label: "Description",
																					type: "textarea",
																					placeholder: "Enter description",
																				},
																				{
																					name: "purpose",
																					label: "Purpose",
																					type: "textarea",
																					placeholder: "Enter purpose",
																				},
																			]}
																			onAdded={async (files) => {
																				const selectedDefendants = Array.isArray(form.getValues("defendants"))
																					? form.getValues("defendants")
																					: form.getValues("defendants")
																						? [form.getValues("defendants")]
																						: [];

																				const targetDefendants =
																					selectedDefendants.length > 0 ? selectedDefendants.map((def) => def.value) : [defendants[0]?.id];

																				setPostingForDefendant(targetDefendants[0]);

																				const newEvidences = files.flatMap((file) =>
																					targetDefendants.map((defendantId) => ({
																						...file,
																						evidence_type: "Documentary",
																						case_record_user_id: defendantId || defendants[0]?.id,
																					})),
																				);
																				form.setValue("evidences", [...evidences.filter((e) => e.evidence_type !== "Documentary"), ...newEvidences]);
																			}}
																			onRowDelete={async (row) => {
																				await expungeCaseMutation.mutateAsync({
																					id: row.id,
																				});
																			}}
																			metadata={{
																				evidence_type: "Documentary",
																			}}
																		/>
																	</div>
																</TabsContent>
																{/* Testimonial */}
																<TabsContent value="testimonial" className="data-[orientation=vertical]:ml-0 px-2">
																	<div className="flex flex-col w-full">
																		<EvidenceList
																			canEdit={true}
																			removeFromList={false}
																			data={evidences?.filter((e) => e.evidence_type === "Testimonial" && e.case_record_user_id === activeDefendantId) || []}
																			schema={[
																				// {
																				//   className:
																				//     "py-2 px-3 text-left text-sm text-foreground-dark",
																				//   name: "exhibit",
																				//   label: "Exhibit",
																				//   type: "text_input",
																				//   placeholder: "Enter exhibit",
																				// },
																				{
																					name: "witness_name",
																					label: "Witness Name",
																					type: "text_input",
																					placeholder: "Enter witness name",
																				},
																				{
																					name: "description",
																					label: "Description",
																					type: "textarea",
																					placeholder: "Enter description",
																				},
																			]}
																			onAdded={async (files) => {
																				const selectedDefendants = Array.isArray(form.getValues("defendants"))
																					? form.getValues("defendants")
																					: form.getValues("defendants")
																						? [form.getValues("defendants")]
																						: [];

																				const targetDefendants =
																					selectedDefendants.length > 0 ? selectedDefendants.map((def) => def.value) : [defendants[0]?.id];

																				setPostingForDefendant(targetDefendants[0]);

																				const newEvidences = files.flatMap((file) =>
																					targetDefendants.map((defendantId) => ({
																						...file,
																						evidence_type: "Testimonial",
																						case_record_user_id: defendantId || defendants[0]?.id,
																					})),
																				);
																				form.setValue("evidences", [...evidences.filter((e) => e.evidence_type !== "Testimonial"), ...newEvidences]);
																			}}
																			onRowDelete={async (row) => {
																				await expungeCaseMutation.mutateAsync({
																					id: row.id,
																				});
																			}}
																			metadata={{
																				evidence_type: "Testimonial",
																			}}
																		/>
																	</div>
																</TabsContent>
																{/* Object */}
																<TabsContent value="object" className="data-[orientation=vertical]:ml-0 px-2">
																	<div className="flex flex-col w-full">
																		<EvidenceList
																			canEdit={true}
																			removeFromList={false}
																			data={evidences?.filter((e) => e.evidence_type === "Object" && e.case_record_user_id === activeDefendantId) || []}
																			schema={[
																				{
																					className: "w-[90px] py-2 px-3 text-left text-sm text-foreground-dark",
																					name: "exhibit",
																					label: "Exhibit",
																					type: "text_input",
																					placeholder: "Enter exhibit",
																				},
																				{
																					name: "description",
																					label: "Description",
																					type: "textarea",
																					placeholder: "Enter description",
																				},
																				{
																					name: "purpose",
																					label: "Purpose",
																					type: "textarea",
																					placeholder: "Enter purpose",
																				},
																			]}
																			onAdded={async (files) => {
																				const selectedDefendants = Array.isArray(form.getValues("defendants"))
																					? form.getValues("defendants")
																					: form.getValues("defendants")
																						? [form.getValues("defendants")]
																						: [];

																				const targetDefendants =
																					selectedDefendants.length > 0 ? selectedDefendants.map((def) => def.value) : [defendants[0]?.id];

																				setPostingForDefendant(targetDefendants[0]);

																				const newEvidences = files.flatMap((file) =>
																					targetDefendants.map((defendantId) => ({
																						...file,
																						evidence_type: "Object",
																						case_record_user_id: defendantId || defendants[0]?.id,
																					})),
																				);

																				form.setValue("evidences", [...evidences.filter((e) => e.evidence_type !== "Object"), ...newEvidences]);
																			}}
																			onRowDelete={async (row) => {
																				await expungeCaseMutation.mutateAsync({
																					id: row.id,
																				});
																			}}
																			metadata={{ evidence_type: "Object" }}
																		/>
																	</div>
																</TabsContent>
															</ScrollArea>
														</Tabs>
													</div>
												</div>
											</div>
											{/* </ScrollArea> */}
											<div className="flex shrink-0 flex-row items-center justify-end w-full h-[65px] px-4 gap-3 border-t border-slate-200 bg-background">
												<Button
													variant="ghost"
													type="button"
													onClick={() => {
														closeModal();
													}}
												>
													Cancel
												</Button>
												<Button
													// disabled={isSubmitting}
													loading={isSubmitting}
													variant={"default"}
												>
													Submit
												</Button>
											</div>
										</div>
									</Panel>
								</PanelGroup>
							</TabsContent>
						</Tabs>
					</div>
				</div>
			</form>
		</Form>
	);
}
